import React from "react";
import { Loader2 } from "lucide-react";

export function PageHeader({ title, subtitle, actions, icon: Icon }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
      <div className="flex items-start gap-3">
        {Icon && (
          <div className="hidden sm:flex h-11 w-11 rounded-lg bg-seq-forest text-seq-green items-center justify-center shrink-0">
            <Icon size={22} />
          </div>
        )}
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-semibold tracking-tight text-slate-900">{title}</h1>
          {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, hint, tone = "default", testId }) {
  const tones = {
    default: "text-slate-900",
    green: "text-seq-green",
    amber: "text-amber-600",
    red: "text-red-600",
    teal: "text-seq-teal",
  };
  return (
    <div data-testid={testId} className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider font-medium text-slate-500">{label}</span>
        {Icon && <Icon size={18} className="text-slate-400" />}
      </div>
      <div className={`text-2xl font-display font-semibold mt-2 ${tones[tone]}`}>{value}</div>
      {hint && <div className="text-xs text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

export function SectionCard({ title, action, children, className = "", noPad = false }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-xl ${className}`}>
      {(title || action) && (
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <h3 className="font-display font-semibold text-slate-800 text-sm">{title}</h3>
          {action}
        </div>
      )}
      <div className={noPad ? "" : "p-4"}>{children}</div>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, hint, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-14 text-center">
      {Icon && <Icon size={40} className="text-slate-300 mb-3" />}
      <p className="text-slate-600 font-medium">{title}</p>
      {hint && <p className="text-sm text-slate-400 mt-1 max-w-sm">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Loading({ label = "Loading…" }) {
  return (
    <div className="flex items-center justify-center py-20 text-slate-400 gap-2">
      <Loader2 className="animate-spin" size={20} />
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function Field({ label, children, className = "", required }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <label className="text-sm font-medium text-slate-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}
